import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import {
  useDownloadAllDocumentsZip,
  useGetAssessmentById,
  useGetAssessmentCompareVersions,
  useGetAssessmentResults,
  useGetAssessmentVersionHistory,
  useGetReportDownloadUrl,
} from '@features/assessment';
import {
  DomainResultsList,
  OldVersionBanner,
  PriorityActionsPanel,
  ResultsHeader,
  ResultsStatsCards,
} from '@features/assessment/components/overview';
import { mapToAssessmentOverviewViewModel } from '@features/assessment/utils/assessment-mapper';
// Direct path, not the feature barrel: the barrel also pulls in the
// remediation pages, which import back into @features/assessment — a cycle
// Vite's dev server refuses to resolve.
import { RemediationCtaBanner } from '@features/remediation/components/RemediationCtaBanner';
import { ContentLanguageProvider, useFeatureFlag } from '@hooks';
import { AssessmentStatus, FeatureFlagKey } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { Assessment, DomainComparisonEntry } from '@libs/types';

export const AssessmentOverviewPage: FC = observer(function AssessmentOverviewPage() {
  const { assessmentId: urlAssessmentId } = useParams<{ assessmentId: string }>();

  // Active version starts as the URL's assessmentId; user can switch via VersionSelector.
  const [activeVersionId, setActiveVersionId] = useState<string | undefined>(urlAssessmentId);
  // Compare-to baseline ('' = none). Held here, next to the reset effect that
  // clears it, and handed down to the body.
  const [compareToId, setCompareToId] = useState<string>('');

  // Reset internal state when navigating to a new URL.
  useEffect(() => {
    setActiveVersionId(urlAssessmentId);
    setCompareToId('');
  }, [urlAssessmentId]);

  // This guard is why the page body lives in its own component below.
  // `activeVersionId` is state, not a route param, so it really does flip
  // between renders — the effect above sets it. Any hook placed after an early
  // return like this one therefore runs on some renders and not others, which
  // is React error #310, "Rendered more hooks than during the previous render".
  // Bailing out above a component boundary unmounts the body instead.
  if (!urlAssessmentId || !activeVersionId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Trans i18nKey="app.assessments.error.invalidAssessmentId" />
        </Alert>
      </Box>
    );
  }

  return (
    <AssessmentOverviewPageContent
      urlAssessmentId={urlAssessmentId}
      activeVersionId={activeVersionId}
      compareToId={compareToId}
      setCompareToId={setCompareToId}
    />
  );
});

interface AssessmentOverviewPageContentProps {
  urlAssessmentId: string;
  /** Non-optional by construction: the wrapper above bails out when it is missing. */
  activeVersionId: string;
  compareToId: string;
  setCompareToId: Dispatch<SetStateAction<string>>;
}

/** The page body. Every hook below runs unconditionally. */
const AssessmentOverviewPageContent: FC<AssessmentOverviewPageContentProps> = observer(function AssessmentOverviewPageContent({
  urlAssessmentId,
  activeVersionId,
  compareToId,
  setCompareToId,
}: AssessmentOverviewPageContentProps) {
  const isRemediationEnabled = useFeatureFlag(FeatureFlagKey.REMEDIATION);
  const navigate = useNavigate();

  // Controlled tab for the domain results panel (0 = Charts, 1 = Detailed Gaps).
  const [overviewTab, setOverviewTab] = useState(0);
  // Ref to the domain results panel so we can scroll to it programmatically.
  const domainListRef = useRef<HTMLElement>(null);
  // Ref to the outer scroll container — lets us scroll back to the top to
  // point users at the "Start New Version" button.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // When true, the "Start New Version" button briefly pulses to draw attention
  // (triggered when a user clicks "Edit" on a domain but no draft exists yet).
  const [highlightNewVersion, setHighlightNewVersion] = useState(false);

  const handleViewDetailedGaps = useCallback(() => {
    setOverviewTab(1);
    setTimeout(() => {
      domainListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  // No draft yet: scroll to the top and pulse the "Start New Version" button so
  // the user understands they must create a new version before they can edit.
  const handlePromptNewVersion = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setHighlightNewVersion(true);
    // Keep the pulse + hint tooltip up for 5s so the user has time to read it.
    setTimeout(() => setHighlightNewVersion(false), 5000);
  }, []);

  // Fetch assessment metadata for the active version (title, language, version).
  const {
    data: assessment,
    isLoading: isLoadingAssessment,
    isError: isAssessmentError,
    error: assessmentError,
  } = useGetAssessmentById(
    { assessmentId: activeVersionId },
    { includeDomains: true },
  );

  // Fetch results for the active version.
  const {
    data: resultsResponse,
    isLoading: isLoadingResults,
    isError: isResultsError,
    error: resultsError,
  } = useGetAssessmentResults({ assessmentId: activeVersionId });

  // Fetch every sibling version in the family (any status) so the version
  // selector + compare dropdown stay rendered. We narrow to COMPLETED below
  // when populating the comparable list.
  const { data: versionHistory } = useGetAssessmentVersionHistory(urlAssessmentId);

  // Conditionally fetch comparison data when user has picked a baseline.
  const { data: comparison } = useGetAssessmentCompareVersions(
    compareToId || undefined,
    activeVersionId,
  );

  // Report download mutation.
  const reportDownloadMutation = useGetReportDownloadUrl();
  const documentsZipMutation = useDownloadAllDocumentsZip();

  const handleReportDownload = () => {
    reportDownloadMutation.mutate({ assessmentId: activeVersionId });
  };

  const handleDocumentsZipDownload = () => {
    documentsZipMutation.mutate({ assessmentId: activeVersionId });
  };

  // All sibling versions (including the active one) — completed only.
  // Filtering here (rather than at the API call) keeps the dropdowns rendered
  // even when in-flight versions exist; only completed ones get listed because
  // processing/draft/failed siblings have no comparable score yet.
  const allVersions: Assessment[] = useMemo(
    () =>
      (versionHistory?.rows ?? []).filter((v) => v.status === AssessmentStatus.COMPLETED),
    [versionHistory],
  );

  const activeVersion = allVersions.find((v) => v.id === activeVersionId) ?? assessment;
  // allVersions is sorted newest-first and filtered to completed only, so index 0
  // is always the newest completed version — safe to navigate to from the overview page.
  const latestCompletedVersion = allVersions[0];
  const baselineVersion = compareToId ? allVersions.find((v) => v.id === compareToId) : undefined;

  // Build a lookup keyed by the comparison-side assessmentDomain.id.
  const domainComparisonMap = useMemo<Map<string, DomainComparisonEntry>>(() => {
    const m = new Map<string, DomainComparisonEntry>();
    (comparison?.domainComparison ?? []).forEach((entry) => {
      m.set(entry.comparisonAssessmentDomain.id, entry);
    });
    return m;
  }, [comparison]);

  // The in-progress draft sibling (if any). At most one draft can exist per
  // family at a time (see canStartNewVersion). When present, the overview shows
  // a per-domain "Edit" button so the user can jump straight into the draft's
  // workflow to re-upload documents for that domain. We read from the raw
  // version history (not `allVersions`, which is narrowed to completed only).
  const draftVersion = useMemo(
    () => (versionHistory?.rows ?? []).find((v) => v.status === AssessmentStatus.DRAFT),
    [versionHistory],
  );

  // Count domains that changed relative to the baseline.
  const editedDomainsCount = useMemo(() => {
    let count = 0;
    domainComparisonMap.forEach((entry) => {
      if (entry.changedInComparison) count++;
    });
    return count;
  }, [domainComparisonMap]);

  // Loading state.
  if (isLoadingAssessment || isLoadingResults) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state.
  if (isAssessmentError || isResultsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {assessmentError?.message || resultsError?.message || 'Failed to load assessment results'}
        </Alert>
      </Box>
    );
  }

  // Results not available yet.
  if (!resultsResponse?.results || !assessment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          <Typography variant="h6" gutterBottom>
            <Trans i18nKey="assessments.overview.resultsNotReady.title" />
          </Typography>
          <Typography variant="body2">
            <Trans i18nKey="assessments.overview.resultsNotReady.body" />
          </Typography>
        </Alert>
      </Box>
    );
  }

  const viewModel = mapToAssessmentOverviewViewModel(
    resultsResponse.results,
    assessment.title || 'Untitled Assessment',
    assessment.language.label,
    assessment.assessmentDomains,
  );

  return (
    // Results are AI prose written once in the assessment's language. Declaring
    // it here lets the generated blocks below pin themselves to it, while the
    // page chrome around them keeps following the UI language.
    <ContentLanguageProvider language={assessment.language.code}>
    <Box
      ref={scrollContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100vh',
        backgroundColor: 'background.default',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {/* Header — embeds VersionSelector. Sticky so the assessment title and
          actions stay visible while scrolling the results. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: 'background.default',
        }}
      >
        <ResultsHeader
          assessmentTitle={viewModel.assessmentTitle}
          assessmentId={activeVersionId}
          language={viewModel.language}
          domainsCovered={viewModel.overallResults.domainsCovered}
          documentsAssessed={viewModel.overallResults.documentsAssessed}
          onExportClick={handleReportDownload}
          isExportLoading={reportDownloadMutation.isPending}
          onDownloadDocumentsClick={handleDocumentsZipDownload}
          isDocumentsDownloading={documentsZipMutation.isPending}
          activeAssessment={assessment}
          canStartNewVersion={
            !!assessment.isLatest &&
            assessment.status === AssessmentStatus.COMPLETED &&
            !draftVersion
          }
          highlightNewVersion={highlightNewVersion}
          allVersions={allVersions.length > 1 ? allVersions : undefined}
          onVersionSelect={(id) => {
            setCompareToId('');
            navigate(routes.assessmentOverview(id));
          }}
          baselineVersion={baselineVersion}
          onChangeCompareTo={allVersions.length > 1 ? setCompareToId : undefined}
        />
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, py: 3 }}>
        <Box sx={{ margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Old-version banner — shown when the active version is not the latest */}
          {latestCompletedVersion && activeVersionId !== latestCompletedVersion.id && activeVersion && (
            <OldVersionBanner
              activeVersion={activeVersion}
              latestVersion={latestCompletedVersion}
              onJumpToLatest={() => {
                setCompareToId('');
                navigate(routes.assessmentOverview(latestCompletedVersion.id));
              }}
            />
          )}

          {/* AI remediation CTA — renders itself only when this assessment has
              gaps to close (or already has a run in flight). Suppressed on an
              older version: remediation can only start from the latest one, so
              offering it there would 409. Also hidden outright when the
              runtime remediation feature flag is off. */}
          {isRemediationEnabled &&
            (!latestCompletedVersion || activeVersionId === latestCompletedVersion.id) && (
              <RemediationCtaBanner assessmentId={activeVersionId} />
            )}

          {/* KPI stats */}
          <ResultsStatsCards
            results={viewModel.overallResults}
            language={viewModel.language}
            activeVersion={activeVersion}
            baselineVersion={baselineVersion}
            comparisonOverall={comparison?.baseline.overallResults}
            editedDomainsCount={editedDomainsCount}
            onViewDetailedGaps={handleViewDetailedGaps}
          />

          {/* Priority Actions — worst 3 domains with AI remediation steps */}
          <PriorityActionsPanel domainResults={viewModel.domainResults} />

          {/* Detailed domain breakdown with Gaps / Charts tabs inside */}
          <Box ref={domainListRef}>
          <DomainResultsList
            domainResults={viewModel.domainResults}
            overallResults={viewModel.overallResults}
            comparisonOverall={comparison?.baseline.overallResults}
            domainComparisonMap={domainComparisonMap}
            activeVersion={activeVersion}
            baselineVersion={baselineVersion}
            draftVersion={draftVersion}
            onRequestNewVersion={handlePromptNewVersion}
            allVersions={allVersions.length > 1 ? allVersions : undefined}
            hasMultipleVersions={allVersions.length > 1}
            compareToId={compareToId}
            activeVersionId={activeVersionId}
            activeTab={overviewTab}
            onTabChange={setOverviewTab}
          />
          </Box>
        </Box>
      </Box>
    </Box>
    </ContentLanguageProvider>
  );
});
